import JSZip from "jszip";
import { DOMParser } from "xmldom";
import { Location } from "./custom-map";

export interface Polygon {
  coordinates: Array<[number, number]>;
}

export async function parseKMZFile(url?: string): Promise<Polygon[]> {
  try {
    const response = await fetch(url || "/service-area.kmz");
    const blob = await response.blob();

    const zip = await JSZip.loadAsync(blob);
    const contents = zip;

    // KMZ files typically contain a doc.kml file
    const kmlContent = await contents.file("doc.kml")?.async("text");
    if (!kmlContent) throw new Error("No KML content found");

    const parser = new DOMParser();
    const kmlDoc = parser.parseFromString(kmlContent, "text/xml");

    // Extract coordinates from KML
    const coordinates = kmlDoc.getElementsByTagName("coordinates");

    const polygons: Polygon[] = [];

    for (let i = 0; i < coordinates.length; i++) {
      const coordText = coordinates[i].textContent?.trim();

      const coords = coordText?.split(" ") || [];
      const parsedCoords = coords
        .filter((coord) => coord.length > 0)
        .map((coord) => {
          const [lng, lat] = coord.split(",");
          // Add validation
          if (!lng || !lat) return null;
          const parsedLng = parseFloat(lng);
          const parsedLat = parseFloat(lat);
          if (isNaN(parsedLng) || isNaN(parsedLat)) return null;
          return [parsedLat, parsedLng] as [number, number];
        })
        .filter((coord): coord is [number, number] => coord !== null);

      if (parsedCoords.length > 0) {
        polygons.push({ coordinates: parsedCoords });
      }
    }

    return polygons;
  } catch (error) {
    console.error("Error parsing KMZ file:", error);
    return [];
  }
}

export function isPointInPolygon(
  point: [number, number],
  polygon: Polygon
): boolean {
  const [lat, lng] = point;
  let inside = false;

  // Ray casting algorithm
  for (
    let i = 0, j = polygon.coordinates.length - 1;
    i < polygon.coordinates.length;
    j = i++
  ) {
    const [yi, xi] = polygon.coordinates[i]; // Note: KML coordinates are [lng,lat]
    const [yj, xj] = polygon.coordinates[j];

    // Check if point crosses polygon edge
    const intersect =
      yi > lat !== yj > lat && // Point is between y coordinates
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi; // Point is left of edge

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

export function findNearestLocation(
  userLat: number,
  userLng: number,
  locations: Location[]
): Location | null {
  if (locations.length === 0) return null;

  let nearest = locations[0];
  let minDistance = calculateDistance(
    userLat,
    userLng,
    nearest.lat,
    nearest.lng
  );

  for (const location of locations) {
    const distance = calculateDistance(
      userLat,
      userLng,
      location.lat,
      location.lng
    );
    if (distance < minDistance) {
      minDistance = distance;
      nearest = location;
    }
  }

  return nearest;
}

function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

export async function parseKMLFile(url?: string): Promise<Polygon[]> {
  try {
    const response = await fetch(url || "/service-area.kml");
    const kmlContent = await response.text();

    const parser = new DOMParser();
    const kmlDoc = parser.parseFromString(kmlContent, "text/xml");

    // Extract coordinates from KML
    const coordinates = kmlDoc.getElementsByTagName("coordinates");

    const polygons: Polygon[] = [];

    for (let i = 0; i < coordinates.length; i++) {
      const coordText = coordinates[i].textContent?.trim();

      const coords = coordText?.split(" ") || [];
      const parsedCoords = coords
        .filter((coord) => coord.length > 0)
        .map((coord) => {
          const [lng, lat] = coord.split(",");
          // Add validation
          if (!lng || !lat) return null;
          const parsedLng = parseFloat(lng);
          const parsedLat = parseFloat(lat);
          if (isNaN(parsedLng) || isNaN(parsedLat)) return null;
          return [parsedLat, parsedLng] as [number, number];
        })
        .filter((coord): coord is [number, number] => coord !== null);

      if (parsedCoords.length > 0) {
        polygons.push({ coordinates: parsedCoords });
      }
    }

    return polygons;
  } catch (error) {
    console.error("Error parsing KML file:", error);
    return [];
  }
}

export async function parseMapFile(url?: string): Promise<Polygon[]> {
  if (!Boolean(url)) {
    return parseKMZFile();
  }

  const isKMZ = url?.toLowerCase().endsWith(".kmz");

  if (isKMZ) {
    return parseKMZFile(url);
  } else {
    return parseKMLFile(url);
  }
}
